/**
 * Datas de campos personalizados (`<input type="date">`) são dias de calendário, sem hora.
 * `new Date('2026-05-22')` e `toISOString()` interpretam o valor em UTC e, em fusos negativos
 * como o do Brasil, deslocam o dia — por isso o parse/format aqui é sempre local.
 */

const pad2 = (value: number): string => String(value).padStart(2, '0');

/** Date (hora local) para o formato aceito por `<input type="date">`. */
export function formatDateInputLocal(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Valor de um campo de data para meia-noite local. Retorna null se não for uma data válida. */
export function parseDateInputLocal(rawValue: unknown): Date | null {
  if (rawValue === undefined || rawValue === null) return null;

  if (rawValue instanceof Date) {
    if (isNaN(rawValue.getTime())) return null;
    return new Date(rawValue.getFullYear(), rawValue.getMonth(), rawValue.getDate());
  }

  const trimmed = String(rawValue).trim();
  if (!trimmed) return null;

  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }
    return parsed;
  }

  const fallback = new Date(trimmed);
  if (isNaN(fallback.getTime())) return null;
  return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
}

/** Hoje à meia-noite local. */
export function startOfTodayLocal(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function addDaysLocal(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

/**
 * O prefill só pode reagir a um prazo mínimo novo, nunca ao campo estar vazio no momento.
 * Enquanto o usuário digita, o `<input type="date">` reporta valor vazio a cada segmento
 * incompleto (ex.: dia "0"); repreencher aí sobrescreveria o que está sendo digitado.
 *
 * Com prazo novo: se veio de uma edição do usuário (ex.: mudou o Orçamento Estimado),
 * a data é reposicionada mesmo já preenchida; se veio de carga inicial ou de rascunho,
 * só preenche o campo vazio, para não descartar a data que já estava salva.
 */
export function shouldPrefillDateField(params: {
  currentValue: unknown;
  minDateTime: number;
  lastPrefillMinDateTime: number | undefined;
  triggeredByUserEdit: boolean;
}): boolean {
  const { currentValue, minDateTime, lastPrefillMinDateTime, triggeredByUserEdit } = params;
  if (lastPrefillMinDateTime === minDateTime) return false;
  if (triggeredByUserEdit) return true;
  return (
    currentValue === undefined || currentValue === null || String(currentValue).trim() === ''
  );
}
